// History tab: every version with its review trail, restore for retired
// versions, "start a draft from here", and a unit-level diff of the selected
// version against live.

import { useEffect, useMemo, useState } from "react";
import { ensureDraft, errorText, getVersion, restoreVersion } from "../../services/talkTracks";
import type { TalkTrackContent, TalkTrackTopic, VersionSummary } from "../../types/talkTrack";
import { emptyContent } from "../../types/talkTrack";
import { formatDateTime } from "../analytics/format";
import { diffContent, type DiffUnit } from "./format";
import { VersionBadge } from "./TopicBadges";

interface Props {
  topic: TalkTrackTopic;
  versions: VersionSummary[];
  selectedVersion: number;
  // The editor's copy of the selected version (includes unsaved edits).
  content: TalkTrackContent;
  dirty: boolean;
  onSelect: (version: number) => void;
  onFocusUnit: (unitId: string) => void;
  // After restore / new draft; `select` switches the editor to that version.
  onDone: (message: string, select?: number) => Promise<void> | void;
}

function DiffList({ title, units, onFocusUnit }: { title: string; units: DiffUnit[]; onFocusUnit?: (id: string) => void }) {
  if (units.length === 0) return null;
  return (
    <div className="tt-diff-group">
      <div className="tt-side-sub">{title} ({units.length})</div>
      <ul className="tt-diff">
        {units.map((u) => (
          <li key={u.id}>
            <span className="tt-unit-kind">{u.kind}</span>{" "}
            {onFocusUnit ? (
              <button className="tt-link mono" onClick={() => onFocusUnit(u.id)}>{u.id}</button>
            ) : (
              <span className="mono">{u.id}</span>
            )}{" "}
            <span className="muted">{u.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HistoryTab({ topic, versions, selectedVersion, content, dirty, onSelect, onFocusUnit, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<TalkTrackContent | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);

  const liveVersion = topic.liveVersion;
  const compareToLive = liveVersion !== null && liveVersion !== selectedVersion;

  useEffect(() => {
    if (!compareToLive || liveVersion === null) return;
    let cancelled = false;
    setLive(null);
    setLiveError(null);
    getVersion(topic.id, liveVersion)
      .then((d) => !cancelled && setLive(d.version.content))
      .catch((e) => !cancelled && setLiveError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [topic.id, liveVersion, compareToLive]);

  // Nothing live yet: every unit counts as added.
  const base = useMemo(() => (liveVersion === null ? emptyContent() : live), [liveVersion, live]);
  const diff = useMemo(() => (base ? diffContent(base, content) : null), [base, content]);
  // Topic-level fields that aren't units: named, since there is no row to list.
  const fieldNotes = diff
    ? [
        diff.summaryChanged && "Summary changed",
        diff.entrySignalsChanged && "Entry signals changed",
        diff.bridgeChanged && "Bridge changed",
        diff.closingsChanged && "Closing lines changed",
      ].filter((n): n is string => Boolean(n))
    : [];

  async function run(label: string, action: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onDone(message);
    } catch (e) {
      setError(`${label} failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function startDraft(from: number) {
    setBusy(true);
    setError(null);
    try {
      const draft = await ensureDraft(topic.id, from);
      await onDone(`Draft v${draft.version} started from v${from}`, draft.version);
    } catch (e) {
      setError(`Start draft failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  function restore(v: number) {
    if (!confirm(`Make v${v} live again for everyone? Live v${liveVersion ?? "—"} retires.`)) return;
    run("Restore", () => restoreVersion(topic.id, v), `v${v} restored — it's live ✓`);
  }

  const hasDraft = versions.some((v) => v.status === "draft");

  return (
    <div className="tt-history">
      {error && <div className="error-text" style={{ marginBottom: 8 }}>{error}</div>}
      {versions.length === 0 ? (
        <div className="state-box">No versions yet.</div>
      ) : (
        <div className="table-scroll">
          <table className="guide-table">
            <thead>
              <tr>
                <th className="no-sort">version</th>
                <th className="no-sort">status</th>
                <th className="no-sort">author</th>
                <th className="no-sort">review</th>
                <th className="no-sort">dates</th>
                <th className="no-sort"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version} className={v.version === selectedVersion ? "selected" : ""}>
                  <td>
                    <strong>v{v.version}</strong>
                    {v.basedOn !== null && <div className="muted tt-small">from v{v.basedOn}</div>}
                  </td>
                  <td>
                    <VersionBadge status={v.status} />
                    {v.version === topic.liveVersion && <div className="tt-small tt-live">live</div>}
                    {v.version === topic.candidateVersion && (
                      <div className="tt-small tt-live">candidate · {topic.rolloutPct}%</div>
                    )}
                  </td>
                  <td className="mono voice-cell">{v.authorUid || "—"}</td>
                  <td className="voice-cell">
                    {v.reviewerUid ? <span className="mono">{v.reviewerUid}</span> : "—"}
                    {v.reviewNote && <div className="tt-review-note">“{v.reviewNote}”</div>}
                  </td>
                  <td className="voice-cell tt-dates">
                    <div>created {formatDateTime(v.createdAt)}</div>
                    {v.submittedAt && <div>submitted {formatDateTime(v.submittedAt)}</div>}
                    {v.approvedAt && <div>approved {formatDateTime(v.approvedAt)}</div>}
                    {v.publishedAt && <div>published {formatDateTime(v.publishedAt)}</div>}
                  </td>
                  <td>
                    <div className="row-actions">
                      {v.version !== selectedVersion && (
                        <button className="btn btn-sm" onClick={() => onSelect(v.version)}>Open</button>
                      )}
                      {v.status === "retired" && (
                        <button className="btn btn-sm" disabled={busy} onClick={() => restore(v.version)}>Restore</button>
                      )}
                      {v.status !== "draft" && !hasDraft && (
                        <button className="btn btn-sm" disabled={busy} onClick={() => startDraft(v.version)}
                          title={`New draft copied from v${v.version}`}>
                          Start draft from v{v.version}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasDraft && (
        <div className="muted tt-small" style={{ marginTop: 6 }}>
          A draft is already open — submit or edit it before starting another.
        </div>
      )}

      <div className="panel-head" style={{ marginTop: 18 }}>
        Changes in v{selectedVersion} vs {liveVersion !== null ? `live v${liveVersion}` : "nothing live"}
      </div>
      {dirty && <div className="muted tt-small">Includes unsaved edits.</div>}
      {liveVersion === selectedVersion ? (
        <div className="state-box">This is the live version.</div>
      ) : liveError ? (
        <div className="state-box error">Couldn't load live v{liveVersion}: {liveError}</div>
      ) : !diff ? (
        <div className="state-box">Loading live version…</div>
      ) : diff.added.length + diff.removed.length + diff.changed.length === 0 &&
        fieldNotes.length === 0 ? (
        <div className="state-box">No differences.</div>
      ) : (
        <div className="tt-diff-wrap">
          {fieldNotes.length > 0 && <div className="muted tt-small">{fieldNotes.join(" · ")}</div>}
          <DiffList title="Added" units={diff.added} onFocusUnit={onFocusUnit} />
          <DiffList title="Changed" units={diff.changed} onFocusUnit={onFocusUnit} />
          <DiffList title="Removed" units={diff.removed} />
        </div>
      )}
    </div>
  );
}
